import api from '../../services/api';

/*
 * The supplies of a warehouse: boxes, tape, film, labels.
 *
 * They are NOT merchandise. The kardex answers for somebody else's goods and
 * every balance in it has a company that owns it; a cardboard box belongs to
 * whoever runs the warehouse. So supplies keep a book of their own, and here —
 * unlike with stock — **seeing and managing are the same thing**: what the
 * operator pays for its boxes is not its client's business, and the server only
 * answers the supplies of the warehouses this session holds.
 */

/**
 * Not paginated: answers every supply that matches, as `{ supplies, count }`.
 * Each one carries `stock_value` and `margin`, which are sums and not fields,
 * and its `client_prices`.
 *
 * @param {object} params - `warehouse, search, is_active ('1'|'0')`.
 */
export const getSuppliesRequest = async (params) => {
  return await api.get('warehouses/supplies/', { params });
};

/** @param {object} payload - `warehouse_id` plus `sku, name, barcode, default_price, is_active`. */
export const createSupplyRequest = async (payload) => {
  return await api.post('warehouses/supplies/create/', payload);
};

/**
 * The card only. Balance and average cost are deliberately not writable: they
 * move with a replenishment or an adjustment, which leave a trail.
 */
export const updateSupplyRequest = async (supplyId, payload) => {
  return await api.patch(`warehouses/supplies/${supplyId}/`, payload);
};

/**
 * A purchase coming in. The only movement that recalculates the average cost.
 *
 * @param {{quantity: string|number, unit_cost: string|number, reference?: string}} payload
 */
export const replenishSupplyRequest = async (supplyId, payload) => {
  return await api.post(`warehouses/supplies/${supplyId}/replenish/`, payload);
};

/**
 * Corrects the balance without touching the cost. `quantity` is the signed
 * difference, and `notes` — the reason — is mandatory.
 *
 * @param {{quantity: string|number, notes: string}} payload
 */
export const adjustSupplyRequest = async (supplyId, payload) => {
  return await api.post(`warehouses/supplies/${supplyId}/adjust/`, payload);
};

/** The last 200 movements, newest first, as `{ movements }`. */
export const getSupplyHistoryRequest = async (supplyId) => {
  return await api.get(`warehouses/supplies/${supplyId}/history/`);
};

/**
 * What each client is charged. An empty price removes the exception and sends
 * that client back to the default one.
 *
 * @param {number} supplyId
 * @param {{[companyId: number]: number|null}} prices
 */
export const saveSupplyPricesRequest = async (supplyId, prices) => {
  return await api.put(`warehouses/supplies/${supplyId}/prices/`, { prices });
};
